import { Trophy, Warning, ArrowsClockwise } from "@phosphor-icons/react";

export default function SoccerGamePanel({ game }) {
  const away = game.awayTeam || {};
  const home = game.homeTeam || {};
  const details = game.details || [];
  const teamStats = game.teamStats || [];
  const awayStats = (teamStats.find(t => t.teamId === away.id) || {}).stats || {};
  const homeStats = (teamStats.find(t => t.teamId === home.id) || {}).stats || {};

  const statRows = [
    { label: "Possession", away: awayStats["possessionPct"], home: homeStats["possessionPct"], unit: "%" },
    { label: "Shots", away: awayStats["totalShots"] || awayStats["shots"], home: homeStats["totalShots"] || homeStats["shots"] },
    { label: "Shots on Target", away: awayStats["shotsOnTarget"], home: homeStats["shotsOnTarget"] },
    { label: "Corners", away: awayStats["cornerKicks"], home: homeStats["cornerKicks"] },
    { label: "Fouls", away: awayStats["fouls"], home: homeStats["fouls"] },
    { label: "Offsides", away: awayStats["offsides"], home: homeStats["offsides"] },
    { label: "Yellow Cards", away: awayStats["yellowCards"], home: homeStats["yellowCards"] },
    { label: "Saves", away: awayStats["saves"], home: homeStats["saves"] },
  ].filter(r => r.away || r.home);

  const goals = details.filter(d => d.isGoal);
  const cards = details.filter(d => d.isCard);

  return (
    <div className="space-y-4" data-testid="soccer-panel">
      {/* Match timeline */}
      {details.length > 0 && (
        <div className="bg-[#141414] border border-white/10 rounded-xl p-4" data-testid="match-timeline">
          <h3 className="font-['Bebas_Neue'] text-lg uppercase tracking-wider text-white mb-3">Match Events</h3>
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/10" />
            <div className="space-y-2">
              {details.map((d, idx) => {
                const isAway = d.teamId === away.id;
                return (
                  <div key={idx} className={`flex items-center gap-2 ${isAway ? "flex-row" : "flex-row-reverse"}`}>
                    <div className={`flex-1 ${isAway ? "text-right pr-4" : "text-left pl-4"}`}>
                      <span className="text-xs text-white/80 font-medium">{d.athlete}</span>
                      <span className="text-[10px] text-white/30 ml-2">{d.type}</span>
                    </div>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 z-10 ${
                      d.isGoal ? "bg-[#34C759]/20 border border-[#34C759]/50" : 
                      d.isCard ? "bg-[#FF9500]/20 border border-[#FF9500]/50" : 
                      "bg-white/10 border border-white/20"
                    }`}>
                      {d.isGoal && <Trophy size={12} weight="fill" className="text-[#34C759]" />}
                      {d.isCard && <Warning size={12} weight="fill" className="text-[#FF9500]" />}
                      {d.isSub && <ArrowsClockwise size={12} className="text-white/50" />}
                      {!d.isGoal && !d.isCard && !d.isSub && <span className="text-[8px] text-white/40">+</span>}
                    </div>
                    <div className={`flex-1 ${isAway ? "text-left pl-4" : "text-right pr-4"}`}>
                      <span className="score-font text-xs text-[#007AFF] font-bold">{d.clock}'</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Possession bar */}
      {(awayStats["possessionPct"] || homeStats["possessionPct"]) && (
        <div className="bg-[#141414] border border-white/10 rounded-xl p-4" data-testid="possession">
          <h3 className="text-[10px] tracking-widest uppercase text-white/40 mb-3 text-center">Possession</h3>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              {away.logo && <img src={away.logo} alt="" className="w-5 h-5 object-contain" />}
              <span className="score-font text-lg text-white font-bold">{awayStats["possessionPct"] || "0"}%</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="score-font text-lg text-white font-bold">{homeStats["possessionPct"] || "0"}%</span>
              {home.logo && <img src={home.logo} alt="" className="w-5 h-5 object-contain" />}
            </div>
          </div>
          <div className="flex h-3 rounded-full overflow-hidden">
            <div className="h-full transition-all" style={{
              width: `${awayStats["possessionPct"] || 50}%`,
              backgroundColor: away.color ? `#${away.color}` : "#007AFF",
            }} />
            <div className="h-full transition-all" style={{
              width: `${homeStats["possessionPct"] || 50}%`,
              backgroundColor: home.color ? `#${home.color}` : "#FF3B30",
            }} />
          </div>
        </div>
      )}

      {/* Stats comparison */}
      {statRows.length > 0 && (
        <div className="bg-[#141414] border border-white/10 rounded-xl overflow-hidden" data-testid="soccer-stats">
          <div className="px-4 py-2.5 border-b border-white/5">
            <h3 className="text-[10px] tracking-widest uppercase font-bold text-white/40">Match Stats</h3>
          </div>
          <div className="divide-y divide-white/5">
            {statRows.map((row) => (
              <div key={row.label} className="flex items-center justify-between px-4 py-2 text-xs">
                <span className="score-font text-white/70 w-12 text-right font-semibold">{row.away || "0"}{row.unit || ""}</span>
                <span className="text-white/40 uppercase tracking-wider text-[10px] flex-1 text-center">{row.label}</span>
                <span className="score-font text-white/70 w-12 font-semibold">{row.home || "0"}{row.unit || ""}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
